import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Popover } from 'antd';
import { CSSProperties, Key, useEffect, useState } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { arycolor } from '../../../assets/css/color';
import { aryballeColorPalette, DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE, defaultColorPalette } from '../../../compute/colormap';
import { getPeptideSetType, spots2peptides } from '../../../compute/utils';
import {
  selectAggregatePeptides,
  selectBoundariesMap,
  selectCurrentRecordID,
  selectExcludedRecordIDs,
  selectRecords,
  selectSessionID,
  selectSubtractItemName,
  selectExcludedPeptides,
  selectExcludedSpots,
  selectHumidityCompensationCalibrantName,
  selectHumidityCompensationMethod,
  selectHumidityCompensationPositionOffset,
  selectHumidityCompensationSubstractionGain,
} from '../../../features/analysisConfig/analysisConfigSlice';
import { PeptideSet, Signature } from '../../../types/analysisTypes';
import { FeatureFlag, UserClaimsWithTSDB } from '../../../types/userType';
import { fetchAuthorizedAPIEndpoint } from '../../../utils';
import PeptidesSelectorGrid from './PeptidesSelector/PeptidesSelectorGrid';
import SpotsSelectorGrid from './PeptidesSelector/SpotsSelectorGrid';
import { Contrasts } from '../../../types/analysisTypes';
import { getContrastThresholdFromType } from '../../../compute/contrast';
import { WithFeatureFlagsCheck } from '../../../with_feature_flags_check';
import { selectVisibleSettings } from '../../../features/analysisConfig/sessionInfoSlice';

type PeptidesSelectorSettingsProps = {};

type typeStyle = {
  [K in Key]: CSSProperties;
};

enum PeptidesState {
  SelectedPeptides = 'Selected',
  UnselectedPeptides = 'Unselected',
  UnselectedSomeSpots = 'Partially selected',
  InactivePeptides = 'Inactive',
  weakContrastSpots = 'Weak Optical Contrast',
}

interface SubsetMetrics {
  Step: number[];
  Count: number[];
  Inter: number[];
  Intra: number[];
  Ratio: number[];
  Sensors: string[][];
  Removed: string[];
}
export interface SensorSubset {
  Sensors: string[];
  Metrics: SubsetMetrics;
}

const PeptidesSelectorSettings: React.FC<PeptidesSelectorSettingsProps> = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(true);

  const visibleSettings = useAppSelector(selectVisibleSettings);
  const sessionID = useAppSelector(selectSessionID);
  const currentRecordID = useAppSelector(selectCurrentRecordID);
  const records = useAppSelector(selectRecords);
  const aggregatePeptides = useAppSelector(selectAggregatePeptides);
  const boundariesMap = useAppSelector(selectBoundariesMap);
  const subtractItemName = useAppSelector(selectSubtractItemName);
  const excludedRecordIDs = useAppSelector(selectExcludedRecordIDs);
  // const excludedSpots = useAppSelector(selectExcludedSpots);
  // const excludedPeptides = useAppSelector(selectExcludedPeptides);

  const humidityCalibrationCalibrantName = useAppSelector(selectHumidityCompensationCalibrantName);
  const humidityCalibrationPositionOffset = useAppSelector(selectHumidityCompensationPositionOffset);
  const humidityCalibrationSubstractionGain = useAppSelector(selectHumidityCompensationSubstractionGain);
  const humidityCompensationMethod = useAppSelector(selectHumidityCompensationMethod);

  const [error, setError] = useState('');
  const [formatedSpots, setFormatedSpots] = useState<{ X: number[]; Y: number[]; Z: string[][] }>();
  const [spots, setSpots] = useState<string[]>();
  const [peptidesSetType, setPeptidesSetType] = useState<PeptideSet>(PeptideSet.Unknown);
  const [weakContrastSpots, setWeakContrastSpots] = useState<string[]>([]);
  const [sensorAutoSelectResult, setSensorAutoSelectResult] = useState<SensorSubset>();

  const [userInfo, setUserInfo] = useState<UserClaimsWithTSDB | null>(null);
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | undefined>();

  useEffect(() => {
    getAccessTokenSilently().then((token) => {
      setAccessToken(token);
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!user || !isAuthenticated) setUserInfo(null);
    else setUserInfo(user as UserClaimsWithTSDB);
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!accessToken) return;

    var boundaries = boundariesMap[currentRecordID];
    if (boundaries === undefined) {
      return;
    }
    if (!userInfo) return;
    fetchAuthorizedAPIEndpoint(`/compute/signature?session_id=${sessionID}&record_id=${currentRecordID}`, accessToken, {
      method: 'POST',
      body: JSON.stringify({
        sessionID,
        boundariesMap,
        aggregatePeptides: !(userInfo && userInfo.feature_flags.includes(FeatureFlag.AANonAggregatedPeptidesAccessEnabled)),
        subtractItemName,
        excludedRecordIDs,
      }),
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          throw resp.json();
        }
      })
      .then((data: Signature) => {
        setError('');
        if (aggregatePeptides === false) setFormatedSpots(formatSpots(data.Peptides));
        else setSpots(data.Peptides);

        if (data.Peptides) {
          setPeptidesSetType(
            getPeptideSetType(
              data.Peptides.map((p) => Number(parseInt(p.toString()))),
              true
            )
          );
        } else setPeptidesSetType(PeptideSet.Unknown);
      })
      .catch((e) => {
        Promise.resolve(e).then((resp: { Reason: string }) => {
          setError(resp.Reason);
        });
      });
  }, [accessToken, sessionID, currentRecordID, boundariesMap, aggregatePeptides, records, subtractItemName, excludedRecordIDs, userInfo]);

  useEffect(() => {
    if (!accessToken) return;
    if (sessionID !== '') {
      fetchAuthorizedAPIEndpoint(`/compute/mmi/constrast?session_id=${sessionID}&record_id=${currentRecordID}`, accessToken)
        .then((resp) => {
          if (resp.ok) {
            return resp.json();
          } else {
            throw new Error();
          }
        })
        .then((x: Contrasts) => {
          if (x !== null) {
            let _weakContrastSpots = x.Sensors.filter((v, i) => x.Values[i] < getContrastThresholdFromType(x.Type));
            _weakContrastSpots = _weakContrastSpots.map((s) => s.toString().split('[')[1].split(']')[0]);
            setWeakContrastSpots(_weakContrastSpots);
          }
        });
    }
  }, [accessToken, sessionID, currentRecordID]);

  useEffect(() => {
    // reset previous subset found
    setSensorAutoSelectResult(undefined);

    if (!visibleSettings || !isDropdownOpen || !accessToken || sessionID === '') return;

    fetchAuthorizedAPIEndpoint(`/compute/sensor/subset?session_id=${sessionID}`, accessToken, {
      method: 'POST',
      body: JSON.stringify({
        sessionID,
        boundariesMap,
        excludedRecordIDs,
        aggregatePeptides: true,
        subtractItemName,
        excludedPeptides: [],
        excludedSpots: weakContrastSpots,
        humidityCompensation: {
          calibrantName: humidityCalibrationCalibrantName,
          positionOffset: humidityCalibrationPositionOffset,
          SubstractionGain: humidityCalibrationSubstractionGain,
          Method: humidityCompensationMethod,
        },
      }),
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          throw new Error();
        }
      })
      .then((x: SensorSubset) => {
        if (x !== null) {
          setSensorAutoSelectResult(x);
        }
      });
  }, [
    accessToken,
    sessionID,
    visibleSettings,
    isDropdownOpen,
    boundariesMap,
    excludedRecordIDs,
    subtractItemName,
    weakContrastSpots,
    humidityCalibrationCalibrantName,
    humidityCalibrationPositionOffset,
    humidityCalibrationSubstractionGain,
    humidityCompensationMethod,
  ]);

  const formatSpots = (spots: string[]) => {
    let [peptideCodeStrs, peptideCoordinateInts] = spots2peptides(spots);
    let rowNb = Math.max(...peptideCoordinateInts.map((e) => e[0]));
    let colNb = Math.max(...peptideCoordinateInts.map((e) => e[1]));

    let X: number[] = [];
    let Y: number[] = [];
    let Z: string[][] = [];

    for (let colIdx = 0; colIdx < colNb + 1; colIdx++) X.push(colIdx);
    for (let rowIdx = 0; rowIdx < rowNb + 1; rowIdx++) Y.push(rowIdx);

    for (let rowIdx = 0; rowIdx < rowNb + 1; rowIdx++) {
      let row: string[] = [];
      for (let colIdx = 0; colIdx < colNb + 1; colIdx++) {
        row.push('');
      }
      Z.push(row);
    }
    peptideCoordinateInts.forEach(([row, col], i) => (Z[row][col] = peptideCodeStrs[i]));
    return { X, Y, Z };
  };

  if (error !== '') {
    return (
      <div>
        <Alert type="error" message={error} />
      </div>
    );
  }

  const style: typeStyle = {
    base: { width: 50, height: 32, textAlign: 'center', lineHeight: '32px' },
    baseLegend: { width: 40, height: 27, textAlign: 'center', lineHeight: '27px', color: 'white', background: aryballeColorPalette.cyan, fontSize: 14 },
    th: { color: 'black', fontWeight: 500 },
    td: { background: aryballeColorPalette.cyan, cursor: userInfo && !userInfo.feature_flags.includes(FeatureFlag.AALightViewQualityControl) ? 'pointer' : 'not-allowed', color: 'white' },
    peptidesBroken: { background: '#EFEFEF', cursor: 'initial', border: '1px solid #EFEFEF' },
    selectedPeptides: { background: DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE, border: `1px solid ${DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE}` },
    diselectedPeptides: { background: 'white', border: '1px solid #CCD0D4', color: aryballeColorPalette.blueDark, fontWeight: 500 },
    diselectedSomePeptides: { background: 'white', border: `2px solid ${DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE}`, color: DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE, fontWeight: 700, minWidth: '40px' },
    selectedWeakContrastSpots: { border: `2px solid ${defaultColorPalette.volcano}` },
    diselectedWeakContrastSpots: { background: 'white', border: `2px solid ${defaultColorPalette.volcano}`, color: defaultColorPalette.volcano, fontWeight: 700, minWidth: '40px' },
  };

  return (
    <div style={{ marginBottom: 30 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>{aggregatePeptides ? 'Peptides' : 'Spots'} selector</span>
        <Popover
          style={{ display: 'flex' }}
          trigger={'hover'}
          content={
            <div>
              Permits to control the <b>biosensor set</b> of the data.
              <br></br>
              Note: <b>no peptide nor spot alignment</b> is done automatically in case of datasets with different PORs
              <br></br>
              For now it is the user's responsibility to respect the spotfile
            </div>
          }
        >
          <FontAwesomeIcon
            icon="info-circle"
            className="clickable-icon"
            style={{
              color: arycolor.aryBlue,
              marginLeft: 20,
            }}
          />
        </Popover>
        {isDropdownOpen ? (
          <FontAwesomeIcon icon="caret-down" style={{ marginLeft: 10, cursor: 'pointer' }} onClick={() => setIsDropdownOpen(false)} />
        ) : (
          <FontAwesomeIcon icon="caret-right" style={{ marginLeft: 10, cursor: 'pointer' }} onClick={() => setIsDropdownOpen(true)} />
        )}
      </div>

      {isDropdownOpen && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!aggregatePeptides ? (
            <SpotsSelectorGrid style={style} spots={formatedSpots} peptidesSetType={peptidesSetType} weakContrastSpots={weakContrastSpots} />
          ) : (
            <PeptidesSelectorGrid style={style} spots={spots} peptidesSetType={peptidesSetType} weakContrastSpots={weakContrastSpots} sensorAutoSelectResult={sensorAutoSelectResult} />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 32 }}>
            <div style={{ display: 'flex', margin: 2.5, alignItems: 'center' }}>
              <td style={{ ...style.baseLegend, ...style.selectedPeptides }}>12</td>
              <span style={{ marginLeft: 15 }}>{PeptidesState.SelectedPeptides}</span>
            </div>
            <div style={{ display: 'flex', margin: 2.5, alignItems: 'center' }}>
              <td style={{ ...style.baseLegend, ...style.diselectedPeptides }}>31</td>
              <span style={{ marginLeft: 15 }}>{PeptidesState.UnselectedPeptides}</span>
            </div>
            {!aggregatePeptides && (
              <div style={{ display: 'flex', margin: 2.5, alignItems: 'center' }}>
                <td style={{ ...style.baseLegend, ...style.diselectedWeakContrastSpots }}>31</td>
                <span style={{ marginLeft: 15 }}>{PeptidesState.weakContrastSpots}</span>
              </div>
            )}
            {aggregatePeptides && (
              <WithFeatureFlagsCheck ff={FeatureFlag.AANonAggregatedPeptidesAccessEnabled}>
                <div style={{ display: 'flex', margin: 2.5, alignItems: 'center' }}>
                  <td style={{ ...style.baseLegend, ...style.diselectedSomePeptides }}>31</td>
                  <span style={{ marginLeft: 15 }}>{PeptidesState.UnselectedSomeSpots}</span>
                </div>
              </WithFeatureFlagsCheck>
            )}

            <div style={{ display: 'flex', margin: 2.5, alignItems: 'center' }}>
              <td style={{ ...style.baseLegend, ...style.peptidesBroken }}></td>
              <span style={{ marginLeft: 15 }}>{PeptidesState.InactivePeptides}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeptidesSelectorSettings;
